import React, { useEffect, useState, useMemo, useRef } from "react";
import { instance2 } from "../utils/axiosInstance";
import { formatNumber } from "../numberingformat";
import { Label } from "src/components/shadcn/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "src/components/shadcn/popover";
import { Button } from "src/components/shadcn/button";
import {
  CalendarIcon,
  Check,
  ChevronsUpDown,
  Loader2Icon,
  Plus,
} from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "src/components/shadcn/command";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "src/components/shadcn/dialog";
import { cn } from "src/lib/utils";
import { format } from "date-fns";
import { id } from "date-fns/locale";
import { Calendar } from "src/components/shadcn/calendar";
import { Card, CardContent } from "../shadcn/card";
import { Input } from "../shadcn/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../shadcn/select";
import { Separator } from "../shadcn/separator";
import { toast } from "sonner";
import { Textarea } from "../shadcn/textarea";
import { OrderTambahanList } from "./ordertambahanlist";
import { parseInputToNumericString } from "src/lib/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Badge } from "../shadcn/badge";
import { Skeleton } from "../shadcn/skeleton";

const fetchIngredients = async () => {
  const response = await instance2.get("/ingredient_master");
  return response.data;
};

const fetchSuppliers = async () => {
  const response = await instance2.get("/supplier");
  return response.data;
};

const fetchExtraOrders = async () => {
  const kitchenId = localStorage.getItem("kitchenId");
  const response = await instance2.get(`/order_draft_add/kitchen/${kitchenId}`);
  return response.data;
};

const fetchUoQs = async () => {
  const response = await instance2.get("/ingredient_uoq_master");
  return response.data.map((uoq) => ({ ...uoq, id: uoq.id.toString() }));
};

const fetchCategories = async () => {
  const response = await instance2.get("ingredient_category_master");
  return response.data.map((cat) => ({ ...cat, id: cat.id.toString() }));
};

const fetchFrequencies = async () => {
  const response = await instance2.get("frequency_master");
  return response.data.map((freq) => ({ ...freq, id: freq.id.toString() }));
};

export default function OrderTambahan() {
  const queryClient = useQueryClient();
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedOption, setSelectedOption] = useState("");
  const [selectedSupplier, setSelectedSupplier] = useState("");
  const [quantity, setQuantity] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [supplierQuery, setSupplierQuery] = useState("");
  const [priceUnit, setPriceUnit] = useState("");
  const [supplierName, setSupplierName] = useState("");
  const [selectedUoq, setSelectedUoq] = useState("");
  const [isBahanSelectionOpen, setIsBahanSelectionOpen] = useState(false);
  const [isSupplierSelectionOpen, setIsSupplierSelectionOpen] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [isAddBahanDialogOpen, setIsAddBahanDialogOpen] = useState(false);
  const [newBahanFormData, setNewBahanFormData] = useState({
    category_id: "",
    uoq_id: "",
    name: "",
    multiple_qty: "1",
    frequency_id: "",
    buffer: "",
    description: "",
    unit_price: "",
    total_price: "",
  });
  const [isAddCategoryDialogOpen, setIsAddCategoryDialogOpen] = useState(false);
  const [newCategory, setNewCategory] = useState("");
  const [newCategoryDescription, setNewCategoryDescription] = useState("");
  const kitchenId = localStorage.getItem("kitchenId");

  const { data: dropdownOptions = [] } = useQuery({
    queryKey: ["ingredients"],
    queryFn: fetchIngredients,
  });

  const { data: supplierOptions = [] } = useQuery({
    queryKey: ["suppliers"],
    queryFn: fetchSuppliers,
  });

  const {
    data: extraOrders = [],
    isPending,
    refetch: fetchExtraOrderData,
  } = useQuery({
    queryKey: ["extraOrders"],
    queryFn: fetchExtraOrders,
  });

  const { data: uoqOptions = [] } = useQuery({
    queryKey: ["uoqs"],
    queryFn: fetchUoQs,
  });

  const { data: categories = [] } = useQuery({
    queryKey: ["categories"],
    queryFn: fetchCategories,
    enabled: isAddBahanDialogOpen, // Only fetch when dialog is open
  });

  const { data: frequencies = [] } = useQuery({
    queryKey: ["frequencies"],
    queryFn: fetchFrequencies,
    enabled: isAddBahanDialogOpen, // Only fetch when dialog is open
  });

  const addIngredientMutation = useMutation({
    mutationFn: (newIngredient) =>
      instance2.post("ingredient_master", newIngredient),
    onSuccess: (response) => {
      toast.success("Bahan baru berhasil ditambahkan!");
      setIsAddBahanDialogOpen(false);
      queryClient.invalidateQueries(["ingredients"]);
      const newIngredient = response.data[0];
      setSelectedOption(newIngredient.id);
      setSearchQuery("");
    },
    onError: (error) => {
      console.error("Error:", error.response?.data || error.message);
      toast.error("Gagal menambahkan bahan baru.");
    },
  });

  const addCategoryMutation = useMutation({
    mutationFn: (newCategory) =>
      instance2.post("/ingredient_category_master", newCategory),
    onSuccess: () => {
      toast.success("Kategori baru berhasil ditambahkan!");
      setIsAddCategoryDialogOpen(false);
      queryClient.invalidateQueries(["categories"]);
      setNewCategory("");
      setNewCategoryDescription("");
    },
    onError: (err) => {
      console.error("Failed to add category:", err);
      toast.error("Gagal menambahkan kategori baru.");
    },
  });

  const addOrderMutation = useMutation({
    mutationFn: (newOrder) => {
      const kitchenId = localStorage.getItem("kitchenId");
      return instance2.post(
        `/order_draft_add_detail/kitchen/${kitchenId}`,
        newOrder,
      );
    },
    onSuccess: () => {
      toast.success("Data berhasil dikirim!");
      fetchExtraOrderData();
      setSelectedUoq("");
      setSelectedOption("");
      setQuantity("");
      setSearchQuery("");
      setPriceUnit("");
    },
    onError: (err) => {
      console.error("Error submitting data:", err);
      toast.error("Gagal mengirim data.");
    },
  });

function parseInputToNumericString(value) {
  if (!value) return "";

  // Remove spaces
  value = String(value).trim();

  // Case 1: value contains a comma → comma is decimal separator
  if (value.includes(",")) {
    // Remove thousand separators (.)
    const cleaned = value.replace(/\./g, "").replace(",", ".");
    return cleaned;
  }

  // Case 2: value has no comma, but might use dots as thousand separators
  // Remove dots entirely
  const cleaned = value.replace(/\./g, "");
  return cleaned;
}


const normalizedQuantityForCalc = quantity ? parseInputToNumericString(quantity) : "";
const normalizedPriceUnitForCalc = priceUnit ? parseInputToNumericString(priceUnit) : "";

const priceTotal =
  normalizedPriceUnitForCalc && normalizedQuantityForCalc
    ? formatNumber(
        Number(normalizedPriceUnitForCalc) * Number(normalizedQuantityForCalc)
      )
    : "";

  useEffect(() => {
    if (selectedOption) {
      const selectedIngredient = dropdownOptions.find(
        (item) => item.id === selectedOption,
      );
      const matchedUoq = uoqOptions.find(
        (uoq) => uoq.name === selectedIngredient?.uoq_name,
      );
      if (matchedUoq) {
        setSelectedUoq(matchedUoq.id.toString());
      } else {
        setSelectedUoq(""); // fallback if not found
      }
    }
  }, [selectedOption, dropdownOptions, uoqOptions]);

  const localDateString = (thedate) => {
    if (!thedate) return "";
    const year = thedate.getFullYear();
    const month = String(thedate.getMonth() + 1).padStart(2, "0");
    const day = String(thedate.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const existingIngredientIdsForSelectedDate = useMemo(() => {
    const formattedSelectedDate = localDateString(selectedDate);
    return new Set(
      extraOrders
        .filter(
          (order) =>
            localDateString(new Date(order.date)) === formattedSelectedDate,
        )
        .map((order) => order.ingredient_id),
    );
  }, [extraOrders, selectedDate]);

  const getWeekRange = (date) => {
    const day = date.getDay();
    const monday = new Date(date);
    const sunday = new Date(date);
    monday.setDate(date.getDate() - ((day + 6) % 7));
    monday.setHours(0, 0, 0, 0);
    sunday.setDate(monday.getDate() + 6);
    sunday.setHours(23, 59, 59, 999);
    return {
      startdate: localDateString(new Date(monday)),
      enddate: localDateString(new Date(sunday)),
    };
  };
  const filteredSupplierOptions = supplierOptions.filter((item) =>
    item.supplier_name.toLowerCase().includes(supplierQuery.toLowerCase()),
  );

  const handleNewBahanChange = (e) => {
    const { name, value } = e.target;
    setNewBahanFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleNewBahanSubmit = async (e) => {
    e.preventDefault();
    const adminId = localStorage.getItem("userAuthentication");
    const dataToSend = {
      ...newBahanFormData,
      category_id: 1,//parseInt(newBahanFormData.category_id, 10),
      uoq_id: parseInt(newBahanFormData.uoq_id, 10),
      admin_id: adminId,
    };
    addIngredientMutation.mutate(dataToSend);
  };
  
/* inside your component */
const inputRef = useRef(null);
// --- paste/replace these two functions in your component ---

/**
 * Format raw input to display:
 * - thousand separator: '.' (every 3 digits in integer part)
 * - decimal separator: ',' (keep decimal part as typed)
 *
 * Rules:
 * - If there's an explicit ',', treat it as decimal separator.
 * - Else if there's a '.' and the last '.' is followed by 1-2 digits -> treat that last '.' as decimal (convert it to ',').
 * - Otherwise '.' are treated as thousand separators (they will be removed before formatting).
 */
function formatInputValue(raw) {
  raw = String(raw ?? "");

  if (raw === "") return "";

  // 1) Quick sanitize: allow digits, comma and dot only
  // We'll decide which dot (if any) is decimal vs thousand.
  const onlyAllowed = raw.replace(/[^\d.,]/g, "");

  // 2) Decide decimal usage
  if (onlyAllowed.includes(",")) {
    // Comma exists -> comma is decimal separator
    // Remove any dots (thousand separators user may have typed), keep digits and comma
    const cleaned = onlyAllowed.replace(/\./g, "").replace(/,+/g, ",");
    const [intPart = "", decPart = ""] = cleaned.split(",", 2);
    const intDigits = intPart.replace(/^0+(?=\d)/, ""); // strip leading zeros except keep single zero
    const formattedInt = intDigits
      ? intDigits.replace(/\B(?=(\d{3})+(?!\d))/g, ".")
      : "";
    // preserve decimal part as typed (no rounding)
    return decPart !== "" ? `${formattedInt},${decPart}` : formattedInt;
  }

  // No comma present. Check if last '.' likely decimal (e.g. user typed 1.5)
  if (onlyAllowed.includes(".")) {
    const lastDotIndex = onlyAllowed.lastIndexOf(".");
    const digitsAfter = onlyAllowed.length - lastDotIndex - 1;
    // treat last dot as decimal if digits after it are 1 or 2 (common decimal length)
    if (digitsAfter > 0 && digitsAfter <= 2) {
      // convert only the last dot to comma, remove any other dots
      // replace last dot with a temporary marker, remove other dots, then convert marker to comma
      const withMarker = onlyAllowed.replace(/\.(?=[^.]*$)/, "<<<DOT>>>"); // last dot -> marker
      const removedOtherDots = withMarker.replace(/\./g, "");
      const converted = removedOtherDots.replace("<<<DOT>>>", ",");
      const cleaned = converted.replace(/,+/g, ",");
      const [intPart = "", decPart = ""] = cleaned.split(",", 2);
      const intDigits = intPart.replace(/^0+(?=\d)/, "");
      const formattedInt = intDigits
        ? intDigits.replace(/\B(?=(\d{3})+(?!\d))/g, ".")
        : "";
      return decPart !== "" ? `${formattedInt},${decPart}` : formattedInt;
    }
    // otherwise, treat dots as thousand separators: remove them and format integer
    const intDigits = onlyAllowed.replace(/\./g, "");
    const cleanedInt = intDigits.replace(/^0+(?=\d)/, "");
    const formattedInt = cleanedInt
      ? cleanedInt.replace(/\B(?=(\d{3})+(?!\d))/g, ".")
      : "";
    return formattedInt;
  }

  // No comma and no dot: just digits -> format thousands
  const onlyDigits = onlyAllowed.replace(/\D/g, "");
  const cleanedInt = onlyDigits.replace(/^0+(?=\d)/, "");
  const formattedInt = cleanedInt
    ? cleanedInt.replace(/\B(?=(\d{3})+(?!\d))/g, ".")
    : "";
  return formattedInt;
}

/**
 * onChange handler that preserves caret position.
 * Uses formatting rules above and tries to set the caret to a meaningful position
 * after formatting by formatting the substring before the caret and using its length.
 */
const handleQuantityChange = (e) => {
  const el = e.target;
  const raw = String(el.value ?? "");
  const prev = String(quantity ?? "");
  const selectionStart = el.selectionStart ?? raw.length;

  // if user just typed a comma or dot at caret-1 position, and previously there was no decimal
  const charJustTyped = raw[selectionStart - 1] ?? "";
  const isCommaTyped = charJustTyped === ",";
  const isDotTyped = charJustTyped === ".";

  // helper that formats whole input (re-uses your formatInputValue)
  const formattedFull = formatInputValue(raw);

  // Special-case: user typed ',' or '.' and there was no decimal before -> insert decimal separator and preserve caret after it
  if ((isCommaTyped || isDotTyped) && !prev.includes(",")) {
    // Format the integer part *before* the separator, to maintain thousand separators
    const before = raw.slice(0, selectionStart - 1); // text before the typed separator
    const formattedBefore = formatInputValue(before);

    // Build new value: formatted integer + comma (decimal marker)
    const newValue = formattedBefore === "" ? "0," : `${formattedBefore},`;

    setQuantity(newValue);

    // caret should be right after the comma
    const caretPos = newValue.length;

    window.requestAnimationFrame(() => {
      if (inputRef.current && typeof inputRef.current.setSelectionRange === "function") {
        try {
          inputRef.current.setSelectionRange(caretPos, caretPos);
        } catch (err) {
          // ignore
        }
      }
    });

    return;
  }

  // Normal path: format full input and restore caret based on formatted prefix
  const before = raw.slice(0, selectionStart);
  const formattedBefore = formatInputValue(before);

  setQuantity(formattedFull);

  window.requestAnimationFrame(() => {
    if (inputRef.current && typeof inputRef.current.setSelectionRange === "function") {
      try {
        // place caret at end of formattedBefore (but not past full length)
        const pos = Math.min(formattedBefore.length, formattedFull.length);
        inputRef.current.setSelectionRange(pos, pos);
      } catch (err) {
        // ignore
      }
    }
  });
};


  const handleSubmitNewCategory = async () => {
    if (!newCategory.trim()) return;
    const adminId = localStorage.getItem("userAuthentication");
    addCategoryMutation.mutate({
      name: newCategory,
      description: newCategoryDescription,
      admin_id: adminId,
    });
  };

  const handleAddIngredient = (name) => {
    setNewBahanFormData({
      category_id: "",
      uoq_id: "",
      name: name,
      multiple_qty: "",
      frequency_id: "",
      buffer: "",
      description: "",
    });
    setIsAddBahanDialogOpen(true);
  };

const handleSubmit = async () => {
  const kitchenId = localStorage.getItem("kitchenId");
  const adminId = localStorage.getItem("userAuthentication");

  if (!kitchenId || !adminId) {
    alert("ID dapur atau admin tidak ditemukan.");
    return;
  }

    const { startdate, enddate } = getWeekRange(selectedDate);
  const qtyNumeric = quantity ? parseInputToNumericString(quantity) : "";
  const priceUnitNumeric = priceUnit ? parseInputToNumericString(priceUnit) : "";

  // Calculate numeric total (as a plain number string) to send to backend
  const priceTotalNumeric =
    priceUnitNumeric && qtyNumeric
      ? String(Number(priceUnitNumeric) * Number(qtyNumeric))
      : "";
  const data = {
    startdate,
    enddate,
    admin_id: adminId,
    ingredient_id: selectedOption,
    qty: qtyNumeric, // normalized qty (e.g. "1.5" or "1500")
    date: localDateString(new Date(selectedDate)),
    uoq_id: selectedUoq, // include user-selected UoQ
    price_unit: priceUnitNumeric,
    price_total: priceTotalNumeric,
    supplier_id: selectedSupplier,
    supplier_name: supplierName,
  };

  addOrderMutation.mutate(data);
  };

  const sortedOrders = [...extraOrders].sort(
    (a, b) => new Date(b.date) - new Date(a.date),
  );

  const groupedPaginatedOrders = sortedOrders.reduce((acc, order) => {
    const date = format(new Date(order.date), "PPP", { locale: id });
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(order);
    return acc;
  }, {});

  const ordersToRender = Object.entries(groupedPaginatedOrders).sort(
    (a, b) => new Date(b[0]) - new Date(a[0]),
  );

  const [enabledDates, setEnabledDates] = useState([]); // Store enabled dates from API
// Fetch API data with enabled dates
const fetchEnabledDates = async () => {
  try {
    const response = await instance2.get(`menu_schedule_dates/${kitchenId}`);
    const data = response.data; // Access data from the response object

    // Strip the time from the date and store it as YYYY-MM-DD
      const enabledDates = data
      .filter(item => 
        // item.po_exists === "N" && 
        item.saved === "Y")
      .map(item => localDateString(new Date(item.date))); // Convert the date to "YYYY-MM-DD"

    setEnabledDates(enabledDates); // Set the enabled dates
  } catch (error) {
    console.error("Error fetching data:", error);
  }
};

// IDs from /ingredient_location that must be hidden from "Pilih Bahan"
const [scheduledIngredientIds, setScheduledIngredientIds] = useState([]);
const scheduledIdsSet = useMemo(
  () => new Set(scheduledIngredientIds.map(Number)),
  [scheduledIngredientIds]
);
useEffect(() => {
  if (!selectedDate) {
    setScheduledIngredientIds([]);
    return;
  }

  const locationId = localStorage.getItem("locationId") || "1"; // fallback to 1 if needed
  const day = localDateString(new Date(selectedDate));

  (async () => {
    try {
      const res = await instance2.get(
        `/ingredient_location/${locationId}/${day}/${day}`
      );

      // Expecting array of rows with ingredient_id
      const ids = Array.isArray(res.data)
        ? res.data.map((row) => Number(row.ingredient_id))
        : [];

      setScheduledIngredientIds(ids);

      // If currently selected option is now excluded, clear it
      setSelectedOption((prev) =>
        prev && ids.includes(Number(prev)) ? "" : prev
      );
    } catch (e) {
      console.error("Failed to fetch ingredient_location:", e);
      setScheduledIngredientIds([]);
    }
  })();
}, [selectedDate]);



const filteredOptions = dropdownOptions
  .filter((item) =>
    item.ingredient_name.toLowerCase().includes(searchQuery.toLowerCase())
  )
  .filter((item) => !scheduledIdsSet.has(Number(item.id)));


useEffect(() => {
  fetchEnabledDates(); // Fetch data when the component is mounted
}, []);

useEffect(() => {
  // need both ingredient and date
  if (!selectedOption || !selectedDate) return;

  const day = localDateString(new Date(selectedDate)); // "YYYY-MM-DD"

  (async () => {
    try {
      const res = await instance2.get(
        `/price/ingredient/${selectedOption}/${day}`
      );

      const apiPrice = res?.data[0]?.price;
      console.log("Fetched price:", apiPrice);
      // Only set as *initial* if the field is empty (don’t override user edits)
      if (apiPrice !== undefined && apiPrice !== null && priceUnit === "") {
        // normalize to numeric digits string, because your input strips non-digits
        const normalized = String(apiPrice).replace(/\D/g, "");
        setPriceUnit(normalized);
      }
    } catch (err) {
      console.error("Failed to fetch price:", err);
      // leave priceUnit as-is so the user can input manually
    }
  })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [selectedOption, selectedDate]);


  return (
    <div className="p-4 size-full">
      <div className="flex flex-row items-center justify-between mb-4">
        <h2 className="blocktext-black text-xl sm:text-2xl my-2 font-medium">
          Order Manual
        </h2>
      </div>

      <Card className="overflow-hidden p-6 space-y-6 h-full max-h-full">
        <CardContent className="flex p-0 w-full gap-x-4 gap-y-4 flex-wrap xl:flex-nowrap">
          {/* Date Picker */}
          <div className="flex flex-col xl:grid xl:grid-rows-2 w-full gap-4">
            <div className="flex items-center lg:flex-nowrap flex-wrap xl:grid xl:grid-cols-3 gap-2">
              <div className="flex flex-col gap-2 w-full xl:w-auto">
                <Label htmlFor="date" className="flex items-center gap-2">
                  Tanggal
                </Label>
                <Popover
                  open={isDatePickerOpen}
                  onOpenChange={setIsDatePickerOpen}
                >
                  <PopoverTrigger asChild>
                    <Button
                      id="date"
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal bg-transparent hover:bg-transparent",
                        !selectedDate && "text-muted-foreground",
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {selectedDate ? (
                        format(selectedDate, "PPP", { locale: id })
                      ) : (
                        <span>Pilih tanggal</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                     <Calendar
                      mode="single"
                      className="min-w-fit w-[--radix-popover-trigger-width]"
                      selected={selectedDate}
                      onSelect={(date) => {
                        setSelectedDate(date);
                        setIsDatePickerOpen(false);
                      }}
                      fromDate={new Date()}
                      captionLayout="dropdown"
                      locale="id"
                      // disabled={(date) => {
                      //   // Format the calendar date to YYYY-MM-DD
                      //   const dateStr = localDateString(date);
                      //   return !enabledDates.includes(dateStr); // Disable the date if it's not in the enabledDates array
                      // }}
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Searchable Dropdown */}
              <div className="flex flex-col gap-2 w-full xl:w-auto">
                <Label htmlFor="supplier-combobox">Cari Supplier:</Label>
                <Popover
                  open={isSupplierSelectionOpen}
                  onOpenChange={setIsSupplierSelectionOpen}
                >
                  <PopoverTrigger asChild>
                    <Button
                      id="supplier-combobox"
                      role="combobox"
                      variant="outline"
                      aria-expanded={isSupplierSelectionOpen}
                      className="w-full justify-between font-normal text-sm bg-transparent hover:bg-transparent"
                    >
                      {supplierOptions?.find(
                        (option) => option.id === selectedSupplier,
                      )?.supplier_name ?? "Pilih supplier..."}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
                    <Command>
                      <CommandInput
                        placeholder="Cari supplier..."
                        value={supplierQuery}
                        onValueChange={setSupplierQuery}
                        className="focus:ring-0 focus:border-0"
                      />
                      <CommandList>
                        <CommandEmpty>
                          <p className="mb-2 text-balance py-3 px-2 text-center text-sm">
                            Supplier "{supplierQuery}" tidak ditemukan.
                          </p>
                        </CommandEmpty>

                        <CommandGroup>
                          {filteredSupplierOptions.map((item) => (
                            <CommandItem
                              key={item.id}
                              value={item.supplier_name}
                              onSelect={() => {
                                setSelectedSupplier(item.id);
                                setSupplierName(item.supplier_name);
                                setSupplierQuery(""); // Clear input
                                setIsSupplierSelectionOpen(false); // Close popover
                              }}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  selectedSupplier === item.id
                                    ? "opacity-100"
                                    : "opacity-0",
                                )}
                              />
                              {item.supplier_name}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Searchable Dropdown */}
              <div className="flex flex-row gap-2 w-full xl:w-auto">
                <div className="flex flex-col gap-2 w-full">
                  <Label htmlFor="ingredient-combobox">Cari Bahan:</Label>
                  <Popover
                    open={isBahanSelectionOpen}
                    onOpenChange={setIsBahanSelectionOpen}
                  >
                    <PopoverTrigger asChild>
                      <Button
                        id="ingredient-combobox"
                        role="combobox"
                        variant="outline"
                        aria-expanded={isBahanSelectionOpen}
                        className="w-full justify-between font-normal text-sm bg-transparent hover:bg-transparent"
                      >
                        {dropdownOptions?.find(
                          (option) => option.id === selectedOption,
                        )?.ingredient_name ?? "Pilih bahan..."}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
                      <Command>
                        <CommandInput
                          placeholder="Cari bahan..."
                          value={searchQuery}
                          onValueChange={setSearchQuery}
                          className="focus:ring-0 focus:border-0"
                        />
                        <CommandList>
                          <CommandEmpty>
                            <div className="py-3 px-2 text-center text-sm">
                              <p className="mb-2 text-balance">
                                Bahan "{searchQuery}" tidak ditemukan.
                              </p>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-pretty break-words"
                                onClick={() => {
                                  handleAddIngredient(searchQuery);
                                  setIsBahanSelectionOpen(false);
                                }}
                              >
                                Tambahkan dan pilih "{searchQuery}"
                              </Button>
                            </div>
                          </CommandEmpty>

                          <CommandGroup>
                            {filteredOptions.map((item) => {
                              const isAdded =
                                existingIngredientIdsForSelectedDate.has(
                                  item.id,
                                );
                              return (
                                <CommandItem
                                  key={item.id}
                                  value={item.ingredient_name}
                                  disabled={isAdded}
                                  onSelect={() => {
                                    if (isAdded) return;
                                    setSelectedOption(item.id);
                                    setPriceUnit("");
                                    setSearchQuery(""); // Clear input
                                    setIsBahanSelectionOpen(false); // Close popover
                                  }}
                                >
                                  <Check
                                    className={cn(
                                      "mr-2 h-4 w-4",
                                      selectedOption === item.id
                                        ? "opacity-100"
                                        : "opacity-0",
                                    )}
                                  />
                                  <div className="flex items-center gap-4 justify-between w-full">
                                    {item.ingredient_name}
                                    {isAdded && (
                                      <Badge variant="outline">Sudah ada</Badge>
                                    )}
                                  </div>
                                </CommandItem>
                              );
                            })}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 xl:grid xl:grid-cols-3 sm:flex sm:items-center sm:gap-2 justify-center w-full">
              <div className="flex flex-col gap-2 w-full xl:col-span-1 sm:col-span-3">
                <Label className="whitespace-nowrap font-medium">Jumlah</Label>
                <div className="relative w-full min-w-48">
<Input
  ref={inputRef}
  type="text"
  inputMode="decimal"
  value={quantity ?? ""}
  onChange={handleQuantityChange}
  className={cn(
    "min-w-24 placeholder:text-sm text-right bg-card",
    selectedUoq === "" ? "pr-[6.25rem]" : "pr-[4.5rem]",
    quantity && "text-right",
  )}
  placeholder="Jumlah"
/>

                  <Select
                    id="unit"
                    value={selectedUoq}
                    onValueChange={(value) => setSelectedUoq(value)}
                  >
                    <SelectTrigger className="gap-2 text-sm absolute right-0 top-0 w-fit min-w-16 px-2 bg-white">
                      <SelectValue placeholder="Satuan" />
                    </SelectTrigger>
                    <SelectContent>
                      {uoqOptions.map((unit) => (
                        <SelectItem key={unit.id} value={String(unit.id)}>
                          {unit.code}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Harga */}
              <div className="flex items-center gap-2 w-full xl:col-span-2">
                <div className="flex flex-col gap-2 w-full">
                  <Label className="whitespace-nowrap font-medium">
                    Harga Satuan
                  </Label>
                  <div className="relative min-w-24">
                    <p
                      className={cn(
                        priceUnit === "" && "hidden",
                        "absolute top-1/2 left-3 -translate-y-1/2 text-sm text-gray-500",
                      )}
                    >
                      Rp.
                    </p>
                    <Input
                      type="text"
                      inputMode="numeric"
                      value={priceUnit ? formatNumber(priceUnit) : ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === null) return;
                        const raw = value.replace(/\D/g, "");
                        setPriceUnit(raw);
                      }}
                      className={cn(
                        "min-w-24 placeholder:text-sm placeholder:text-left bg-card",
                        formatNumber(priceUnit) && "text-right",
                      )}
                      placeholder="Harga Satuan"
                    />
                  </div>
                </div>

                {/* Harga Total */}
                <div className="flex flex-col gap-2 w-full">
                  <Label className="whitespace-nowrap font-medium">
                    Harga Total
                  </Label>
                  <div className="relative min-w-24">
                    <p
                      className={cn(
                        priceTotal === "" && "hidden",
                        "absolute top-1/2 left-3 -translate-y-1/2 text-sm text-gray-500",
                      )}
                    >
                      Rp.
                    </p>
                    <Input
                      type="text"
                      inputMode="numeric"
                      disabled
                      value={priceTotal}
                      className={cn(
                        "min-w-24 placeholder:text-sm placeholder:text-left",
                        formatNumber(priceTotal) && "text-right",
                      )}
                      placeholder="Harga Total"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Button
            className="self-end xl:w-auto w-full"
            onClick={handleSubmit}
            disabled={
              !selectedOption ||
              !selectedSupplier ||
              !quantity ||
              !selectedUoq ||
              addOrderMutation.isPending
            }
          >
            {addOrderMutation.isPending ? (
              <Loader2Icon className="animate-spin" />
            ) : (
              <Plus />
            )}{" "}
            <span className="xl:hidden">Tambah</span>
          </Button>
        </CardContent>
        <Separator />
        <div className="grid grid-cols-1 gap-4">
          {isPending ? (
            <div className="flex flex-col gap-4 w-full overflow-x-hidden">
              <div className="flex items-center gap-4 w-full overflow-x-hidden">
                <Skeleton className="w-48 h-6 shrink-0" />{" "}
                <Separator className="w-full" />
              </div>
              <Skeleton className="w-full h-72" />
            </div>
          ) : ordersToRender.length > 0 ? (
            ordersToRender.map(([date, orders]) => (
              <OrderTambahanList
                key={date}
                date={date}
                orders={orders}
                refetchData={fetchExtraOrderData}
              />
            ))
          ) : (
            <div className="flex flex-col gap-1 w-full justify-center items-center text-gray-500 py-8 h-[22rem] border rounded-lg">
              <p className="text-xl font-semibold">Tidak ada data</p>
              <p>Belum ada order tambahan yang dibuat.</p>
            </div>
          )}
        </div>
      </Card>

      {/* Add Bahan Dialog */}
      <Dialog
        open={isAddBahanDialogOpen}
        onOpenChange={setIsAddBahanDialogOpen}
      >
        <DialogContent className="flex flex-col max-h-[95vh]">
          <DialogHeader>
            <DialogTitle>Tambah Bahan Baku Baru</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4 overflow-y-auto h-full no-scrollbar p-2">
            <div className="flex flex-col w-full gap-4">
              {/* <div className="flex flex-col items-start gap-0">
                <Label className="block text-gray-700 mb-2 w-full sm:w-1/3">
                  Kategori Bahan
                </Label>
                <Select
                  name="category_id"
                  value={String(newBahanFormData.category_id)}
                  onValueChange={(value) => {
                    if (value === "add_new") {
                      setIsAddCategoryDialogOpen(true);
                    } else {
                      handleNewBahanChange({
                        target: { name: "category_id", value },
                      });
                    }
                  }}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Pilih kategori bahan" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((ingredient) => (
                      <SelectItem
                        key={ingredient.id}
                        value={String(ingredient.id)}
                      >
                        {ingredient.name}
                      </SelectItem>
                    ))}
                    <SelectItem value="add_new" className="text-merah-no">
                      + TAMBAH KATEGORI BAHAN BAKU
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div> */}
              <div className="flex flex-col items-start gap-0">
                <Label className="block text-gray-700 mb-2 w-full sm:w-1/3">
                  Nama Bahan Baku
                </Label>
                <Input
                  type="text"
                  name="name"
                  value={newBahanFormData.name}
                  onChange={handleNewBahanChange}
                  placeholder="Masukkan nama bahan baku"
                  className="w-full placeholder:text-sm"
                />
              </div>

              <div className="flex flex-col items-start gap-0">
                <Label className="block text-gray-700 mb-2 w-full sm:w-1/3">
                  Unit
                </Label>
                <Select
                  name="uoq_id"
                  value={String(newBahanFormData.uoq_id)}
                  onValueChange={(value) =>
                    handleNewBahanChange({ target: { name: "uoq_id", value } })
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Pilih Unit" />
                  </SelectTrigger>
                  <SelectContent>
                    {uoqOptions.map((loc) => (
                      <SelectItem key={loc.id} value={String(loc.id)}>
                        {loc.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {/* <div className="flex flex-col items-start gap-0">
                <Label className="block text-gray-700 mb-2 w-full sm:w-1/3">
                  Kelipatan Kuantitas
                </Label>
                <Input
                  type="text"
                  name="multiple_qty"
                  value={newBahanFormData.multiple_qty}
                  onChange={handleNewBahanChange}
                  placeholder="Masukkan kelipatan pemesanan"
                  className="w-full"
                />
              </div> */}
              <div className="flex flex-col items-start gap-0">
                <Label className="block text-gray-700 mb-2 w-full sm:w-1/3">
                  Frekuensi
                </Label>
                <Select
                  name="frequency_id"
                  value={String(newBahanFormData.frequency_id)}
                  onValueChange={(value) =>
                    handleNewBahanChange({
                      target: { name: "frequency_id", value },
                    })
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Pilih frekuensi pengiriman" />
                  </SelectTrigger>
                  <SelectContent>
                    {frequencies.map((freq) => (
                      <SelectItem key={freq.id} value={String(freq.id)}>
                        {freq.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col items-start gap-0">
                <Label className="block text-gray-700 mb-2 w-full sm:w-1/3">
                  Buffer
                </Label>
                <Input
                  type="number"
                  name="buffer"
                  value={newBahanFormData.buffer}
                  onChange={handleNewBahanChange}
                  placeholder="Masukkan jumlah cadangan stock"
                  className="w-full"
                />
              </div>
            </div>
            <div className="flex flex-col gap-0 w-full">
              <Label className="block text-gray-700 mb-2 text-xs">
                Catatan
              </Label>
              <Textarea
                name="description"
                value={newBahanFormData.description}
                onChange={handleNewBahanChange}
                placeholder="Masukkan catatan"
              />
            </div>
          </div>
          <DialogFooter>
            <Button className="w-full" onClick={handleNewBahanSubmit}>
              Simpan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Category Dialog */}
      <Dialog
        open={isAddCategoryDialogOpen}
        onOpenChange={setIsAddCategoryDialogOpen}
      >
        <DialogContent onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>Tambah Kategori Baru</DialogTitle>
          </DialogHeader>
          <Input
            type="text"
            placeholder="Nama kategori"
            value={newCategory}
            onChange={(e) => setNewCategory(e.target.value)}
          />
          <Textarea
            placeholder="Deskripsi kategori (opsional)"
            value={newCategoryDescription}
            onChange={(e) => setNewCategoryDescription(e.target.value)}
            rows={3}
          />
          <DialogFooter className="sm:justify-end">
            <DialogClose asChild>
              <Button variant="secondary">Batal</Button>
            </DialogClose>
            <Button onClick={handleSubmitNewCategory}>Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
